import React, { useState } from 'react';
import { Check, Plus } from 'lucide-react';
interface Integration {
  id: string;
  name: string;
  description: string;
  connected: boolean;
  logo: React.ReactNode;
}
function OutlookLogo() {
  return <div className="w-12 h-12 flex items-center justify-center" data-id="element-346">
      <svg viewBox="0 0 48 48" width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-347">
        <rect width="48" height="48" rx="8" fill="#0078D4" data-id="element-348" />
        <path d="M28 10h12v28H28V10z" fill="#50A0E0" data-id="element-349" />
        <path d="M8 14l20-4v28L8 34V14z" fill="#1565C0" data-id="element-350" />
        <ellipse cx="18" cy="24" rx="6" ry="7" fill="white" opacity="0.9" data-id="element-351" />
        <ellipse cx="18" cy="24" rx="4" ry="5" fill="#0078D4" data-id="element-352" />
        <rect x="28" y="18" width="8" height="2" rx="1" fill="white" opacity="0.8" data-id="element-353" />
        <rect x="28" y="23" width="8" height="2" rx="1" fill="white" opacity="0.8" data-id="element-354" />
        <rect x="28" y="28" width="6" height="2" rx="1" fill="white" opacity="0.8" data-id="element-355" />
      </svg>
    </div>;
}
function GmailLogo() {
  return <div className="w-12 h-12 flex items-center justify-center" data-id="element-356">
      <svg viewBox="0 0 48 48" width="48" height="48" xmlns="http://www.w3.org/2000/svg" data-id="element-357">
        <path d="M6 36V12l18 13L42 12v24H6z" fill="#F44336" data-id="element-358" />
        <path d="M6 12l18 13L42 12H6z" fill="#E53935" data-id="element-359" />
        <path d="M6 12h36l-18 13L6 12z" fill="white" opacity="0.15" data-id="element-360" />
        <rect x="6" y="12" width="36" height="24" rx="3" fill="none" stroke="#E53935" strokeWidth="0" data-id="element-361" />
        <path d="M6 14a2 2 0 012-2h32a2 2 0 012 2v20a2 2 0 01-2 2H8a2 2 0 01-2-2V14z" fill="#FAFAFA" data-id="element-362" />
        <path d="M6 14l18 12 18-12" stroke="#F44336" strokeWidth="2" fill="none" data-id="element-363" />
        <path d="M6 14l18 12 18-12V34H6V14z" fill="white" data-id="element-364" />
        <path d="M6 14l18 12 18-12" fill="none" stroke="#EA4335" strokeWidth="2.5" data-id="element-365" />
        <path d="M6 34V14l18 12 18-12v20" fill="white" data-id="element-366" />
        <path d="M6 14l18 12 18-12V34H6V14z" fill="white" data-id="element-367" />
        <path d="M6 14l18 12 18-12" stroke="#EA4335" strokeWidth="2.5" fill="none" strokeLinecap="round" data-id="element-368" />
      </svg>
    </div>;
}
function QuickBooksLogo() {
  return <div className="w-12 h-12 rounded-xl bg-[#2CA01C] flex items-center justify-center shadow-sm" data-id="element-369">
      <span className="text-white font-black text-xl tracking-tighter" data-id="element-370">QB</span>
    </div>;
}
function NetSuiteLogo() {
  return <div className="w-12 h-12 rounded-xl bg-[#F37021] flex items-center justify-center shadow-sm" data-id="element-371">
      <span className="text-white font-black text-sm tracking-tight" data-id="element-372">NS</span>
    </div>;
}
function GrandCentralLogo() {
  return <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] flex items-center justify-center shadow-sm" data-id="element-373">
      <span className="text-white font-bold text-xs tracking-tight text-center leading-tight" data-id="element-374">
        GC
      </span>
    </div>;
}
function IntegrationCard({
  integration,
  onToggle
}: {
  integration: Integration;
  onToggle: (id: string) => void;
}) {
  return <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow" data-id="element-375">
      <div className="flex items-center gap-4" data-id="element-376">
        {integration.logo}
        <div data-id="element-377">
          <p className="font-semibold text-slate-900 text-sm" data-id="element-378">
            {integration.name}
          </p>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-379">
            {integration.description}
          </p>
        </div>
      </div>
      <button onClick={() => onToggle(integration.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${integration.connected ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-slate-900 text-white hover:bg-slate-700'}`} data-id="element-380">
        {integration.connected ? <>
            <Check className="w-3.5 h-3.5" data-id="element-381" />
            Connected
          </> : <>
            <Plus className="w-3.5 h-3.5" data-id="element-382" />
            Connect
          </>}
      </button>
    </div>;
}
export function Integrations() {
  const [integrations, setIntegrations] = useState<Integration[]>([{
    id: 'outlook',
    name: 'Microsoft Outlook',
    description: 'Send and receive dispute emails via Outlook',
    connected: true,
    logo: <OutlookLogo data-id="element-383" />
  }, {
    id: 'gmail',
    name: 'Gmail',
    description: 'Send and receive dispute emails via Gmail',
    connected: false,
    logo: <GmailLogo data-id="element-384" />
  }, {
    id: 'grand-central',
    name: 'Grand Central',
    description: 'Sync deduction data from Grand Central ERP',
    connected: false,
    logo: <GrandCentralLogo data-id="element-385" />
  }, {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    description: 'Sync invoices and payment records from QBO',
    connected: false,
    logo: <QuickBooksLogo data-id="element-386" />
  }, {
    id: 'netsuite',
    name: 'NetSuite',
    description: 'Connect your NetSuite ERP for real-time data sync',
    connected: false,
    logo: <NetSuiteLogo data-id="element-387" />
  }]);
  const toggle = (id: string) => {
    setIntegrations(prev => prev.map(i => i.id === id ? {
      ...i,
      connected: !i.connected
    } : i));
  };
  const emailIntegrations = integrations.filter(i => ['outlook', 'gmail'].includes(i.id));
  const erpIntegrations = integrations.filter(i => ['grand-central', 'quickbooks', 'netsuite'].includes(i.id));
  return <div className="space-y-8" data-id="element-388">
      <div data-id="element-389">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-390">
          Integrations
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-391">
          Connect your tools to automate dispute workflows.
        </p>
      </div>

      {/* Email */}
      <div data-id="element-392">
        <div className="flex items-center gap-2 mb-4" data-id="element-393">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider" data-id="element-394">
            Email
          </h3>
          <div className="flex-1 h-px bg-slate-200" data-id="element-395" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-396">
          {emailIntegrations.map(integration => <IntegrationCard key={integration.id} integration={integration} onToggle={toggle} data-id="element-397" />)}
        </div>
      </div>

      {/* ERP */}
      <div data-id="element-398">
        <div className="flex items-center gap-2 mb-4" data-id="element-399">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider" data-id="element-400">
            ERP
          </h3>
          <div className="flex-1 h-px bg-slate-200" data-id="element-401" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-402">
          {erpIntegrations.map(integration => <IntegrationCard key={integration.id} integration={integration} onToggle={toggle} data-id="element-403" />)}
        </div>
      </div>
    </div>;
}